import { createLdsMemoryCache, createLdsRequestCache } from "../../components/lds/cache";
import { fetchFamilies, fetchParents } from "./api";
import type {
  ChildSummary,
  EmailFilter,
  FamilySummary,
  NewsletterFilter,
  Parent,
  PortalFilter,
} from "./types";
import { extractParentsFromFamilies } from "./utils/parentList";

export interface ParentOverviewUiState {
  searchQuery: string;
  emailFilters: EmailFilter[];
  portalFilters: PortalFilter[];
  newsletterFilters: NewsletterFilter[];
  scrollY: number;
}

export interface ParentDetailsCacheEntry {
  parent: Parent;
  families: FamilySummary[];
  children: ChildSummary[];
  currentYearFee: number | null;
  updatedAt: number;
}

const defaultOverviewUiState: ParentOverviewUiState = {
  searchQuery: "",
  emailFilters: [],
  portalFilters: [],
  newsletterFilters: [],
  scrollY: 0,
};

const parentsCache = createLdsMemoryCache<Parent[]>();
const parentOverviewUiStateCache = createLdsMemoryCache<ParentOverviewUiState>(defaultOverviewUiState);
const loadParentOverviewOnce = createLdsRequestCache<Parent[]>();
let lastCanViewFamilies = false;
const parentDetailsCache = new Map<string, ParentDetailsCacheEntry>();

export const hasParentOverviewCache = () => parentsCache.has();

export const getCachedParents = () => parentsCache.get();

export const getParentOverviewUiState = () => parentOverviewUiStateCache.get() ?? defaultOverviewUiState;

export const setParentOverviewUiState = (state: Partial<ParentOverviewUiState>) => {
  const current = getParentOverviewUiState();
  parentOverviewUiStateCache.set({
    ...current,
    ...state,
    emailFilters: state.emailFilters ? [...state.emailFilters] : current.emailFilters,
    portalFilters: state.portalFilters ? [...state.portalFilters] : current.portalFilters,
    newsletterFilters: state.newsletterFilters
      ? [...state.newsletterFilters]
      : current.newsletterFilters,
  });
};

export const setCachedParents = (parents: Parent[]) => {
  parentsCache.set(parents);
  parents.forEach((parent) => {
    const existingDetails = parentDetailsCache.get(String(parent.id));
    if (existingDetails) {
      parentDetailsCache.set(String(parent.id), {
        ...existingDetails,
        parent: { ...existingDetails.parent, ...parent },
        updatedAt: Date.now(),
      });
    }
  });
};

export const removeCachedParent = (parentId: string) => {
  parentsCache.update((parents) => parents?.filter((parent) => String(parent.id) !== String(parentId)) ?? parents);
  parentDetailsCache.delete(String(parentId));
};

export const upsertCachedParent = (parent: Parent) => {
  const parentId = String(parent.id);
  const cachedParents = parentsCache.get();
  if (cachedParents) {
    const existingIndex = cachedParents.findIndex((item) => String(item.id) === parentId);
    parentsCache.set(
      existingIndex >= 0
        ? cachedParents.map((item, index) => (index === existingIndex ? { ...item, ...parent } : item))
        : [parent, ...cachedParents]
    );
  }

  const existingDetails = parentDetailsCache.get(parentId);
  if (existingDetails) {
    parentDetailsCache.set(parentId, {
      ...existingDetails,
      parent: { ...existingDetails.parent, ...parent },
      updatedAt: Date.now(),
    });
  }
};

export const getCachedParent = (parentId: string): Parent | null => {
  const id = String(parentId);
  return (
    parentDetailsCache.get(id)?.parent ??
    parentsCache.get()?.find((parent) => String(parent.id) === id) ??
    null
  );
};

export const getCachedParentDetails = (parentId: string) =>
  parentDetailsCache.get(String(parentId)) ?? null;

export const setCachedParentDetails = (parentId: string, entry: Omit<ParentDetailsCacheEntry, "updatedAt">) => {
  parentDetailsCache.set(String(parentId), {
    ...entry,
    updatedAt: Date.now(),
  });
  upsertCachedParent(entry.parent);
};

export const loadParentOverview = async (canViewFamilies: boolean): Promise<Parent[]> => {
  lastCanViewFamilies = canViewFamilies;
  return loadParentOverviewOnce(async () => {
      try {
        const parents = await fetchParents();
        setCachedParents(parents);
        return parents;
      } catch (parentError) {
        const status = (parentError as { response?: { status?: number } }).response?.status;
        if (status !== 403 || !canViewFamilies) throw parentError;
        const parents = extractParentsFromFamilies(await fetchFamilies());
        setCachedParents(parents);
        return parents;
      }
  });
};

export const prefetchParentOverview = () => {
  void loadParentOverview(lastCanViewFamilies).catch(() => {
    // Prefetch failures should not interrupt internal navigation.
  });
};
